import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Check, Layers, Sparkles, ShieldCheck, CalendarCheck, Zap,
} from "lucide-react";
import { useDemo } from "@/components/landing/DemoContext";
import { useAudit } from "@/components/landing/AuditContext";
import { BASES, ADDONS } from "@/config/clinicOS";

/* /products — the catalog page.
   PURE PRODUCT SHOWCASE — no pricing. The job is discovery.
   Pricing & checkout live on /build (Build OS). */
export default function Products() {
  const openDemo = useDemo();
  const openAudit = useAudit();

  return (
    <article data-testid="products-page" className="overflow-hidden">
      {/* HERO */}
      <section className="relative pt-16 pb-8 sm:pt-40 sm:pb-16">
        <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[520px] bg-gradient-to-b from-violet-500/15 via-blue-500/8 to-transparent blur-3xl" />
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
          <p className="hidden sm:inline-flex items-center gap-2 rounded-full border border-violet-500/30 bg-violet-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-violet-600 dark:text-violet-300">
            <span className="h-1.5 w-1.5 rounded-full bg-violet-500" />
            The Clinic OS product family
          </p>
          <h1 className="mt-0 sm:mt-5 max-w-4xl font-display text-[2.05rem] font-black leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
            8 modules that run your clinic —{" "}
            <span className="bg-gradient-to-r from-blue-600 via-violet-600 to-fuchsia-600 bg-clip-text text-transparent">
              pick the ones that fit.
            </span>
          </h1>
          <p className="mt-3 sm:mt-5 max-w-3xl text-[14px] leading-relaxed text-muted-foreground sm:text-lg lg:text-xl">
            Two starting bases (Hub or Hub+) and six specialty add-ons. Each one solves a specific
            problem clinics actually feel — missed calls, scattered KPIs, slow follow-up,
            content overload. Explore what fits how you operate today.
          </p>

          <div className="mt-5 sm:mt-8 flex flex-wrap items-center gap-2 sm:gap-3">
            <a
              href="#bases"
              data-testid="products-page-jump-bases"
              className="group inline-flex h-12 w-full sm:w-auto items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-violet-600 px-6 text-sm font-bold text-white shadow-lg shadow-violet-600/25 transition-all hover:-translate-y-0.5 hover:shadow-xl"
            >
              <Layers className="h-4 w-4" />
              Start with a base
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </a>
            <a
              href="#add-ons"
              data-testid="products-page-jump-addons"
              className="hidden sm:inline-flex h-12 items-center gap-2 rounded-xl border border-border bg-card px-5 text-sm font-semibold transition-all hover:-translate-y-0.5 hover:bg-secondary"
            >
              <Sparkles className="h-4 w-4 text-violet-500" />
              See specialty add-ons
            </a>
            <Link
              to="/build"
              data-testid="products-page-build-link"
              className="hidden sm:inline-flex h-12 items-center gap-1 px-3 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              Or build your OS now
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          {/* Helper banner — desktop only; mobile users scroll directly to bases */}
          <div className="mt-10 hidden gap-3 sm:grid sm:grid-cols-3 sm:gap-4">
            <FitTile
              icon={<Layers className="h-4 w-4" />}
              tone="violet"
              eyebrow="Start small"
              title="Just getting organized"
              detail="Pick Clinic Hub. One inbox, one calendar, one record."
              link="#bases"
            />
            <FitTile
              icon={<Sparkles className="h-4 w-4" />}
              tone="blue"
              eyebrow="Scale operations"
              title="Adding automation & calls"
              detail="Pick Hub+. Then snap on Call IQ AI and AI Workforce."
              link="#bases"
            />
            <FitTile
              icon={<Zap className="h-4 w-4" />}
              tone="emerald"
              eyebrow="Grow revenue"
              title="Filling more chairs"
              detail="Add Growth, Sales Autopilot or Academy when ready."
              link="#add-ons"
            />
          </div>
        </div>
      </section>

      {/* BASES — Hub vs Hub+ side-by-side (NO PRICES) */}
      <section
        id="bases"
        data-testid="products-bases"
        className="relative overflow-hidden border-t border-border bg-gradient-to-b from-slate-50/60 via-white to-slate-50/60 py-20 dark:bg-none dark:from-transparent dark:via-transparent dark:to-transparent sm:py-24"
      >
        <div className="pointer-events-none absolute -left-32 top-20 h-72 w-72 rounded-full bg-violet-500/15 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <div className="max-w-2xl">
            <p className="inline-flex items-center gap-1.5 rounded-full border border-violet-500/30 bg-violet-500/10 px-2.5 py-1 text-[10.5px] font-semibold uppercase tracking-[0.22em] text-violet-600 dark:text-violet-300">
              <span className="h-1.5 w-1.5 rounded-full bg-violet-500" />
              Bases · choose one to start
            </p>
            <h2 className="mt-4 font-display text-3xl font-black leading-[1.06] tracking-tight sm:text-4xl lg:text-5xl">
              Two foundations.{" "}
              <span className="bg-gradient-to-r from-blue-600 via-violet-600 to-fuchsia-600 bg-clip-text text-transparent">One you&apos;ll grow with.</span>
            </h2>
            <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-muted-foreground sm:text-base">
              Both bases include the full 24-capability Clinic OS platform. Hub is the clean
              foundation. Hub+ adds Social Suite, Automations and Call IQ Essential — the
              foundations the AI add-ons build on.
            </p>
          </div>

          <div className="mt-10 grid gap-5 lg:grid-cols-2">
            {BASES.map((b) => {
              const BIcon = b.icon;
              const isHubPlus = b.id === "hub-plus";
              return (
                <motion.div
                  key={b.id}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5 }}
                  data-testid={`products-base-${b.id}`}
                  className={`group relative flex flex-col overflow-hidden rounded-2xl border p-6 transition-all hover:-translate-y-1 ${
                    isHubPlus
                      ? "border-violet-500/40 bg-gradient-to-br from-violet-500/[0.10] via-blue-500/[0.04] to-transparent shadow-lg shadow-violet-500/10"
                      : "border-border bg-card hover:border-foreground/25"
                  }`}
                >
                  {/* glow */}
                  <div className={`pointer-events-none absolute -right-24 -top-24 h-48 w-48 rounded-full ${isHubPlus ? "bg-violet-500/25" : "bg-blue-500/15"} blur-3xl`} />

                  {isHubPlus && (
                    <span className="absolute right-4 top-4 inline-flex items-center rounded-md bg-violet-500 px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.12em] text-white shadow-md shadow-violet-600/30">
                      Most popular
                    </span>
                  )}

                  {/* Header */}
                  <div className="relative flex items-start gap-3 pr-24">
                    <span className={`inline-flex h-12 w-12 flex-none items-center justify-center rounded-2xl bg-gradient-to-br ${b.color} text-white shadow-md ring-1 ring-inset ring-white/15`}>
                      <BIcon className="h-5 w-5" />
                    </span>
                    <div>
                      <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Base · {isHubPlus ? "advanced" : "starter"}</p>
                      <h3 className="font-display text-2xl font-black tracking-tight sm:text-3xl">{b.name}</h3>
                    </div>
                  </div>
                  <p className="relative mt-3 text-[14px] leading-snug text-muted-foreground">{b.tagline}</p>

                  {/* What you get */}
                  <p className="relative mt-5 text-[10.5px] font-bold uppercase tracking-[0.18em] text-muted-foreground">What&apos;s inside</p>
                  <ul className="relative mt-2 space-y-1.5">
                    {b.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-[13px] leading-snug text-foreground/85">
                        <Check className={`mt-0.5 h-3.5 w-3.5 flex-none ${isHubPlus ? "text-violet-500" : "text-blue-500"}`} strokeWidth={3} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Best for */}
                  {b.bestFor && (
                    <p className="relative mt-5 inline-flex items-center gap-1.5 rounded-full bg-secondary/60 px-2.5 py-1 text-[11px] font-semibold text-foreground/80">
                      <span className={`h-1.5 w-1.5 rounded-full ${isHubPlus ? "bg-violet-500" : "bg-blue-500"}`} />
                      Best for · {b.bestFor}
                    </p>
                  )}

                  <div className="relative mt-6 flex flex-wrap gap-2">
                    <Link
                      to={`/products/${b.id}`}
                      data-testid={`products-base-${b.id}-detail`}
                      className={`group/btn inline-flex h-10 items-center justify-center gap-1.5 rounded-xl px-4 text-[13px] font-bold transition-all hover:-translate-y-0.5 ${
                        isHubPlus
                          ? "bg-gradient-to-r from-blue-600 to-violet-600 text-white shadow-md shadow-violet-600/25 hover:shadow-lg"
                          : "bg-foreground text-background hover:bg-foreground/90"
                      }`}
                    >
                      Explore {b.short}
                      <ArrowRight className="h-3 w-3 transition-transform group-hover/btn:translate-x-0.5" />
                    </Link>
                    <Link
                      to={`/build?base=${b.id}`}
                      data-testid={`products-base-${b.id}-build`}
                      className="inline-flex h-10 items-center gap-1.5 rounded-xl border border-border bg-card px-4 text-[13px] font-semibold text-foreground/85 transition-colors hover:bg-secondary"
                    >
                      <CalendarCheck className="h-3.5 w-3.5" />
                      Start with {b.short}
                    </Link>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ADD-ONS — pure catalog */}
      <section
        id="add-ons"
        data-testid="products-addons"
        className="relative overflow-hidden border-t border-border py-20 sm:py-24"
      >
        <div className="pointer-events-none absolute -right-32 top-20 h-72 w-72 rounded-full bg-emerald-500/15 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <div className="max-w-2xl">
            <p className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-[10.5px] font-semibold uppercase tracking-[0.22em] text-emerald-600 dark:text-emerald-400">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              Specialty add-ons · snap on as you grow
            </p>
            <h2 className="mt-4 font-display text-3xl font-black leading-[1.06] tracking-tight sm:text-4xl lg:text-5xl">
              Six modules built for clinics that{" "}
              <span className="bg-gradient-to-r from-emerald-500 via-teal-500 to-blue-500 bg-clip-text text-transparent">scale.</span>
            </h2>
            <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-muted-foreground sm:text-base">
              Each add-on solves a specific problem your team will eventually feel.
              Snap them on top of your base — turn on / off anytime from your dashboard.
            </p>
          </div>

          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {ADDONS.map((a, i) => {
              const AIcon = a.icon;
              return (
                <motion.div
                  key={a.id}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.4, delay: i * 0.04 }}
                >
                  <Link
                    to={`/products/${a.id}`}
                    data-testid={`products-addon-${a.id}`}
                    className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card p-5 transition-all hover:-translate-y-1 hover:border-foreground/25 hover:shadow-lg"
                  >
                    {/* Corner glow on hover */}
                    <div className={`pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full bg-gradient-to-br ${a.color} opacity-[0.10] blur-3xl transition-opacity duration-300 group-hover:opacity-[0.25]`} />

                    <div className="relative flex items-center justify-between">
                      <span className={`inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br ${a.color} text-white shadow-sm ring-1 ring-inset ring-white/15`}>
                        <AIcon className="h-5 w-5" />
                      </span>
                      {a.requiresBase === "hub-plus" ? (
                        <span className="inline-flex items-center gap-1 rounded-md bg-violet-500/15 px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.12em] text-violet-600 dark:text-violet-300">
                          <Zap className="h-2.5 w-2.5" />
                          Needs Hub+
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-md bg-emerald-500/15 px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.12em] text-emerald-600 dark:text-emerald-400">
                          Works with both
                        </span>
                      )}
                    </div>

                    <p className="relative mt-4 font-display text-xl font-black tracking-tight">{a.name}</p>
                    <p className="relative mt-1.5 text-[13.5px] leading-relaxed text-foreground/85">{a.tagline}</p>

                    {a.features?.length > 0 && (
                      <ul className="relative mt-4 space-y-1.5">
                        {a.features.slice(0, 3).map((f) => (
                          <li key={f} className="flex items-start gap-2 text-[12.5px] text-muted-foreground">
                            <Check className={`mt-0.5 h-3 w-3 flex-none ${a.accent}`} strokeWidth={3} />
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {a.bestFor && (
                      <p className="relative mt-4 text-[11px] font-medium text-muted-foreground/85">
                        <span className="font-semibold uppercase tracking-[0.14em] text-muted-foreground">Best for</span> · {a.bestFor}
                      </p>
                    )}

                    <div className="relative mt-auto pt-5">
                      <span className={`inline-flex items-center gap-1 text-[13px] font-bold ${a.accent}`}>
                        Explore {a.name}
                        <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                      </span>
                    </div>

                    {/* Bottom accent bar — appears on hover */}
                    <div className={`absolute inset-x-0 bottom-0 h-[3px] origin-left scale-x-0 bg-gradient-to-r ${a.color} transition-transform duration-500 group-hover:scale-x-100`} />
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HELP ME DECIDE — quick router */}
      <section className="border-t border-border bg-secondary/20 py-16 sm:py-20 dark:bg-transparent">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <div className="max-w-2xl">
            <p className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/30 bg-amber-500/10 px-2.5 py-1 text-[10.5px] font-semibold uppercase tracking-[0.22em] text-amber-600 dark:text-amber-400">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
              Not sure where to start?
            </p>
            <h2 className="mt-4 font-display text-3xl font-black leading-[1.06] tracking-tight sm:text-4xl">
              Two ways to find your fit.
            </h2>
          </div>

          <div className="mt-8 grid gap-4 sm:grid-cols-2 sm:gap-5">
            <button
              type="button"
              onClick={openAudit}
              data-testid="products-decide-audit"
              className="group relative overflow-hidden rounded-2xl border border-violet-500/30 bg-gradient-to-br from-violet-500/[0.10] via-blue-500/[0.05] to-transparent p-6 text-left transition-all hover:-translate-y-1 hover:border-violet-500/55 hover:shadow-md hover:shadow-violet-500/15"
            >
              <div className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full bg-violet-500/20 blur-3xl" />
              <span className="relative inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-blue-500 text-white shadow-md shadow-violet-500/30">
                <Sparkles className="h-5 w-5" />
              </span>
              <p className="relative mt-4 font-display text-lg font-black tracking-tight sm:text-xl">60-second Clinic Audit</p>
              <p className="relative mt-1.5 text-[13.5px] leading-relaxed text-muted-foreground">
                Answer 5 questions — we&apos;ll recommend the right base + add-ons for how you operate today.
              </p>
              <span className="relative mt-4 inline-flex items-center gap-1 text-[13px] font-bold text-violet-600 dark:text-violet-300">
                Take the audit
                <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
              </span>
            </button>

            <button
              type="button"
              onClick={openDemo}
              data-testid="products-decide-demo"
              className="group relative overflow-hidden rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/[0.10] via-teal-500/[0.05] to-transparent p-6 text-left transition-all hover:-translate-y-1 hover:border-emerald-500/55 hover:shadow-md hover:shadow-emerald-500/15"
            >
              <div className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full bg-emerald-500/20 blur-3xl" />
              <span className="relative inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 text-white shadow-md shadow-emerald-500/30">
                <CalendarCheck className="h-5 w-5" />
              </span>
              <p className="relative mt-4 font-display text-lg font-black tracking-tight sm:text-xl">Talk to a human</p>
              <p className="relative mt-1.5 text-[13.5px] leading-relaxed text-muted-foreground">
                Book a 20-minute walkthrough — we&apos;ll show your exact stack on a real clinic and quote your monthly cost.
              </p>
              <span className="relative mt-4 inline-flex items-center gap-1 text-[13px] font-bold text-emerald-600 dark:text-emerald-300">
                Book a Demo
                <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* FINAL CTA — dark cinematic banner (matches Home + Platform) */}
      <section className="border-t border-border bg-gradient-to-b from-white via-slate-50/60 to-slate-100/60 py-16 dark:bg-none dark:from-transparent dark:via-transparent dark:to-transparent sm:py-20">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <div className="relative overflow-hidden rounded-3xl border border-border bg-[#0a0a14] px-8 py-16 text-center sm:px-16 sm:py-24">
            <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-violet-600/30 blur-[120px]" />
            <div className="pointer-events-none absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-blue-600/25 blur-[120px]" />
            <div className="pointer-events-none absolute inset-0 bg-grid opacity-30" />
            <div className="pointer-events-none absolute inset-0 rounded-3xl ring-1 ring-inset ring-white/5" />

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="relative"
            >
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/75 backdrop-blur">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                Two bases · six add-ons · one workflow
              </span>

              <h2 className="mt-6 font-display text-4xl font-black leading-[1.05] tracking-[-0.025em] text-white sm:text-6xl lg:text-7xl">
                Run your clinic.{" "}
                <span className="relative inline-block whitespace-nowrap">
                  <span className="text-white/40">Not your</span>{" "}
                  <span className="relative inline-block text-rose-400">
                    tools
                    <motion.span
                      initial={{ scaleX: 0 }}
                      whileInView={{ scaleX: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: 0.35, ease: "easeOut" }}
                      className="absolute inset-x-0 top-1/2 h-[4px] origin-left -translate-y-1/2 rounded-full bg-rose-400 sm:h-[5px]"
                      style={{ transformOrigin: "left" }}
                      aria-hidden="true"
                    />
                  </span>
                  <span className="text-white/40">.</span>
                </span>
              </h2>

              <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-white/70 sm:text-lg">
                Replace the messy stack with one calm OS. Fill the schedule, engage every
                patient, keep them coming back — without the tab-switching tax.
              </p>

              <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <Link
                  to="/build"
                  data-testid="products-final-cta-build"
                  className="group inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-blue-600 px-8 text-sm font-bold text-white shadow-lg shadow-violet-600/40 ring-1 ring-inset ring-white/10 transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-violet-600/50 sm:w-auto"
                >
                  Build Your OS <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
                <button
                  type="button"
                  data-testid="products-final-cta-demo"
                  onClick={openDemo}
                  className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/5 px-8 text-sm font-semibold text-white backdrop-blur transition-all hover:-translate-y-0.5 hover:border-white/30 hover:bg-white/10 sm:w-auto"
                >
                  <CalendarCheck className="h-4 w-4" /> Book a Demo
                </button>
              </div>

              <div className="mt-7 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-white/55">
                <span className="inline-flex items-center gap-1.5">
                  <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" />
                  HIPAA-ready
                </span>
                <span className="h-1 w-1 rounded-full bg-white/20" />
                <span>SOC 2 aligned</span>
                <span className="h-1 w-1 rounded-full bg-white/20" />
                <span>99.9% uptime</span>
                <span className="h-1 w-1 rounded-full bg-white/20" />
                <span>2-way EMR sync</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </article>
  );
}

/* Small "find your fit" tile for the hero. */
const FitTile = ({ icon, tone, eyebrow, title, detail, link }) => {
  const palette = {
    violet:  { border: "border-violet-500/30",  bg: "bg-violet-500/[0.08]",  text: "text-violet-600 dark:text-violet-300",  iconBg: "from-violet-500 to-blue-500" },
    blue:    { border: "border-blue-500/30",    bg: "bg-blue-500/[0.08]",    text: "text-blue-600 dark:text-blue-300",    iconBg: "from-blue-500 to-cyan-500" },
    emerald: { border: "border-emerald-500/30", bg: "bg-emerald-500/[0.08]", text: "text-emerald-600 dark:text-emerald-300", iconBg: "from-emerald-500 to-teal-500" },
  }[tone];
  return (
    <a
      href={link}
      className={`group relative overflow-hidden rounded-2xl border ${palette.border} ${palette.bg} p-4 transition-all hover:-translate-y-0.5 hover:shadow-md`}
    >
      <div className="flex items-center gap-2">
        <span className={`inline-flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br ${palette.iconBg} text-white shadow-sm`}>
          {icon}
        </span>
        <span className={`text-[10.5px] font-bold uppercase tracking-[0.16em] ${palette.text}`}>{eyebrow}</span>
      </div>
      <p className="mt-3 font-display text-[14.5px] font-black tracking-tight text-foreground">{title}</p>
      <p className="mt-1 text-[12px] leading-snug text-muted-foreground">{detail}</p>
      <ArrowRight className={`absolute right-3 top-3 h-3.5 w-3.5 ${palette.text} opacity-0 transition-all group-hover:translate-x-0.5 group-hover:opacity-80`} />
    </a>
  );
};
